/**
 * PageLayout Component
 * Main page layout combining Header, Sidebar, Content, and Footer
 *
 * Features:
 * - Auto-detects user role from current route
 * - Automatically populates sidebar with role-appropriate navigation
 * - Highlights active navigation items based on current path
 */

import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Header, type HeaderProps } from './Header';
import { Sidebar, type SidebarProps, type SidebarItem } from './Sidebar';
import { Footer, type FooterProps } from './Footer';
import { Breadcrumb, type BreadcrumbItem } from '../common/Breadcrumb';
import { getNavigationByRole, getRoleFromPath, isNavItemActive, filterNavigationByAccess, type NavigationItem } from '../../config/navigation';
import { useAuth } from '../../contexts/AuthContext';
import { getRolePermissions } from '../../types/user';

export interface PageLayoutProps {
  children: ReactNode;
  header?: HeaderProps | false;
  sidebar?: SidebarProps | false;
  footer?: FooterProps | false;
  breadcrumbs?: BreadcrumbItem[];
  pageTitle?: string;
  actions?: ReactNode;
  className?: string;
}

/**
 * Convert NavigationItem to SidebarItem
 */
function convertToSidebarItem(navItem: NavigationItem, currentPath: string): SidebarItem {
  return {
    id: navItem.id,
    label: navItem.label,
    icon: navItem.icon,
    href: navItem.href,
    badge: navItem.badge,
    active: isNavItemActive(navItem, currentPath),
    separator: navItem.separator,
    children: navItem.children?.map((child) => convertToSidebarItem(child, currentPath)),
  };
}

export function PageLayout({
  children,
  header,
  sidebar,
  footer,
  breadcrumbs,
  pageTitle,
  actions,
  className = '',
}: PageLayoutProps) {
  const location = useLocation();
  const currentPath = location.pathname;
  const { user, logout } = useAuth();

  const showHeader = header !== false;
  const showFooter = footer !== false;

  // Auto-detect role and populate sidebar, filtered by user permissions
  const role = getRoleFromPath(currentPath);
  const allNavigationItems = role ? getNavigationByRole(role) : [];
  const userPermissions = user?.role ? getRolePermissions(user.role) : [];
  const navigationItems = filterNavigationByAccess(
    allNavigationItems,
    userPermissions,
    user?.role
  );
  const sidebarItems: SidebarItem[] = navigationItems.map((item) =>
    convertToSidebarItem(item, currentPath)
  );

  // Determine if sidebar should be shown
  const showSidebar = sidebar !== false;

  // Auto-populate sidebar config
  const sidebarConfig: SidebarProps | undefined =
    sidebar === false
      ? undefined
      : typeof sidebar === 'object'
        ? sidebar
        : {
            items: sidebarItems,
          };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Header */}
      {showHeader && (
        <Header
          {...(typeof header === 'object' ? header : {})}
          user={
            user
              ? {
                  name: user.fullName || `${user.firstName} ${user.lastName}`,
                  email: user.email,
                  role: user.role,
                  onLogoutClick: logout,
                }
              : undefined
          }
        />
      )}

      {/* Main Content Area */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        {showSidebar && sidebarConfig && <Sidebar {...sidebarConfig} />}

        {/* Main Content */}
        <main className={`flex-1 overflow-y-auto ${showSidebar ? 'md:ml-0' : ''}`}>
          <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 ${className}`}>
            {/* Breadcrumbs */}
            {breadcrumbs && breadcrumbs.length > 0 && (
              <div className="mb-4">
                <Breadcrumb items={breadcrumbs} />
              </div>
            )}

            {/* Page Header */}
            {(pageTitle || actions) && (
              <div className="mb-6">
                <div className="flex items-center justify-between">
                  {pageTitle && (
                    <h1 className="text-2xl font-bold text-gray-900">{pageTitle}</h1>
                  )}
                  {actions && <div className="flex items-center gap-3">{actions}</div>}
                </div>
              </div>
            )}

            {/* Page Content */}
            <div>{children}</div>
          </div>
        </main>
      </div>

      {/* Footer */}
      {showFooter && <Footer {...(typeof footer === 'object' ? footer : {})} />}
    </div>
  );
}

/**
 * ContentSection Component
 * Reusable content section with optional header and footer
 */
export interface ContentSectionProps {
  children: ReactNode;
  title?: string;
  description?: string;
  actions?: ReactNode;
  footer?: ReactNode;
  className?: string;
}

export function ContentSection({
  children,
  title,
  description,
  actions,
  footer,
  className = '',
}: ContentSectionProps) {
  return (
    <section className={`bg-white rounded-lg border border-gray-200 shadow-sm ${className}`}>
      {(title || description || actions) && (
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              {title && <h2 className="text-lg font-semibold text-gray-900">{title}</h2>}
              {description && <p className="mt-1 text-sm text-gray-600">{description}</p>}
            </div>
            {actions && <div className="ml-4 flex-shrink-0">{actions}</div>}
          </div>
        </div>
      )}

      <div className="px-6 py-4">{children}</div>

      {footer && <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">{footer}</div>}
    </section>
  );
}

/**
 * GridLayout Component
 * Responsive grid layout for dashboard-style pages
 */
export interface GridLayoutProps {
  children: ReactNode;
  columns?: 1 | 2 | 3 | 4;
  gap?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function GridLayout({
  children,
  columns = 3,
  gap = 'md',
  className = '',
}: GridLayoutProps) {
  const columnStyles = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
  };

  const gapStyles = {
    sm: 'gap-4',
    md: 'gap-6',
    lg: 'gap-8',
  };

  return (
    <div className={`grid ${columnStyles[columns]} ${gapStyles[gap]} ${className}`}>
      {children}
    </div>
  );
}
